import React from "react";
import { Box, Button, Typography, CircularProgress, useTheme } from "@mui/material";
import { isAxiosError } from "axios";
import { roundedPanelSx } from "../../overviewPanel";
import { useSuperInvestorHoldings } from "../../../SuperInvestors/hooks/useSuperInvestorHoldings";
import { CompanyHoldingsTables } from "./CompanyHoldingsTables";
import PortfolioDistributionChart from "../../../SuperInvestors/PortfolioDistributionChart";

interface CompanyHoldingsTabProps {
  cik: string | undefined;
  companyName: string;
  ticker: string;
  isProfileLoading: boolean;
}

export const CompanyHoldingsTab: React.FC<CompanyHoldingsTabProps> = ({
  cik,
  companyName,
  ticker,
  isProfileLoading,
}) => {
  const theme = useTheme();

  // Fetch holdings data using the CIK (only when CIK is available)
  const {
    data: holdingsData,
    isLoading: isHoldingsLoading,
    error: holdingsError,
    refetch,
  } = useSuperInvestorHoldings(cik ?? "");

  const statePanelSx = {
    ...roundedPanelSx(theme.palette.mode === "dark"),
    py: { xs: 6, sm: 8 },
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    gap: 1.5,
  };
  const notFound = isAxiosError(holdingsError) && holdingsError.response?.status === 404;

  if (isProfileLoading || isHoldingsLoading) {
    return (
      <Box role="status" sx={statePanelSx}>
        <CircularProgress size={32} />
        <Typography variant="body2" color="text.secondary">
          Loading company holdings…
        </Typography>
      </Box>
    );
  }

  if (holdingsError && !notFound) {
    return (
      <Box role="alert" sx={statePanelSx}>
        <Typography variant="body2" fontWeight={500}>
          Unable to load holdings
        </Typography>
        <Button size="small" onClick={() => void refetch()}>
          Retry
        </Button>
      </Box>
    );
  }

  if (!cik || notFound || !holdingsData?.data?.length) {
    return (
      <Box role="status" sx={statePanelSx}>
        <Typography variant="body2" fontWeight={500} color="text.secondary">
          No holding data available
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      component="section"
      aria-label={`${companyName} holdings`}
      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
    >
      {/* Portfolio Distribution Chart - Full Width */}
      <PortfolioDistributionChart
        data={holdingsData.portfolioDistribution}
        holdings={holdingsData.data}
        totalValue={holdingsData.totalPortfolioValue}
      />

      <CompanyHoldingsTables
        key={`${ticker}-${holdingsData.year}-${holdingsData.quarter}`}
        data={holdingsData}
      />
    </Box>
  );
};
